import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebaseOptions';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDoc,
  increment,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getMetadata,
} from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

export default function Admin() {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);

  // Create Album State
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('productos');
  const [creating, setCreating] = useState(false);

  // Edit/Upload State
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Edit Metadata State
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');

  useEffect(() => {
    fetchAlbums();
    fetchStorageStats();
  }, []);

  useEffect(() => {
    if (selectedAlbum) {
      setEditTitle(selectedAlbum.title);
      setEditCategory(selectedAlbum.category);
    }
  }, [selectedAlbum]);

  const fetchAlbums = async () => {
    const querySnapshot = await getDocs(collection(db, 'albums'));
    const albumsData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // Sort by most recent creation if available, else by title
    setAlbums(albumsData);
  };

  const fetchStorageStats = async () => {
    try {
      const docRef = doc(db, 'content_stats', 'usage');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setTotalBytes(docSnap.data().totalBytes || 0);
      } else {
        // Initialize if not exists
        await setDoc(docRef, { totalBytes: 0 });
        setTotalBytes(0);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleLogout = () => {
    auth.signOut().then(() => navigate('/login'));
  };

  const handleSyncUsage = async () => {
    if (
      !window.confirm(
        'Esto recalculará el espacio usado leyendo todos los archivos. Puede tardar un poco. ¿Continuar?'
      )
    )
      return;

    let totalSize = 0;
    try {
      console.log('Starting sync...');
      const albumsSnap = await getDocs(collection(db, 'albums'));
      const allImages = [];

      albumsSnap.forEach((doc) => {
        const data = doc.data();
        if (data.images && Array.isArray(data.images)) {
          allImages.push(...data.images);
        }
      });

      console.log(`Checking ${allImages.length} files...`);

      // Process in chunks to avoid overwhelming capabilities
      const chunkSize = 5;
      for (let i = 0; i < allImages.length; i += chunkSize) {
        const chunk = allImages.slice(i, i + chunkSize);
        const promises = chunk.map((url) => {
          const storageRef = ref(storage, url);
          return getMetadata(storageRef)
            .then((meta) => meta.size)
            .catch(() => 0); // Ignore missing files
        });
        const sizes = await Promise.all(promises);
        totalSize += sizes.reduce((a, b) => a + b, 0);
      }

      const docRef = doc(db, 'content_stats', 'usage');
      await setDoc(docRef, { totalBytes: totalSize }, { merge: true });
      setTotalBytes(totalSize);
      alert(`Sincronización completa. Uso total: ${formatBytes(totalSize)}`);
    } catch (error) {
      console.error('Sync error:', error);
      alert('Error al sincronizar espacio');
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const slug = title
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '');
      const docRef = await addDoc(collection(db, 'albums'), {
        title,
        category,
        slug,
        images: [],
        createdAt: new Date(),
      });
      // alert('Album creado con éxito');
      setAlbums([
        ...albums,
        { id: docRef.id, title, category, slug, images: [] },
      ]);
      setTitle('');
    } catch (error) {
      console.error('Error creating album: ', error);
      alert('Error creando album');
    }
    setCreating(false);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedAlbum) return;

    setUploading(true);
    try {
      let totalUploadSize = 0;
      const uploadPromises = files.map(async (file) => {
        totalUploadSize += file.size;
        const storageRef = ref(
          storage,
          `albums/${selectedAlbum.slug}/${file.name}`
        );
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      });

      const downloadURLs = await Promise.all(uploadPromises);

      // Update Firestore Album
      const albumRef = doc(db, 'albums', selectedAlbum.id);
      await updateDoc(albumRef, {
        images: arrayUnion(...downloadURLs),
      });

      // Update Local State (Album)
      const updatedAlbum = {
        ...selectedAlbum,
        images: [...(selectedAlbum.images || []), ...downloadURLs],
      };
      updateLocalState(updatedAlbum);

      // Update Stats
      const statsRef = doc(db, 'content_stats', 'usage');
      await setDoc(
        statsRef,
        {
          totalBytes: increment(totalUploadSize),
        },
        { merge: true }
      );
      setTotalBytes((prev) => prev + totalUploadSize);
    } catch (error) {
      console.error('Error uploading images: ', error);
      alert('Error subiendo algunas imágenes');
    }
    setUploading(false);
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('¿Eliminar foto?')) return;

    try {
      const storageRef = ref(storage, photoUrl);

      // Get size before deleting to update stats
      let sizeToDelete = 0;
      try {
        const metadata = await getMetadata(storageRef);
        sizeToDelete = metadata.size;
      } catch (err) {
        console.warn('Could not fetch metadata for delete', err);
      }

      await deleteObject(storageRef);

      const albumRef = doc(db, 'albums', selectedAlbum.id);
      await updateDoc(albumRef, {
        images: arrayRemove(photoUrl),
      });

      const updatedAlbum = {
        ...selectedAlbum,
        images: selectedAlbum.images.filter((img) => img !== photoUrl),
      };
      updateLocalState(updatedAlbum);

      // Update Stats
      if (sizeToDelete > 0) {
        const statsRef = doc(db, 'content_stats', 'usage');
        await setDoc(
          statsRef,
          {
            totalBytes: increment(-sizeToDelete),
          },
          { merge: true }
        );
        setTotalBytes((prev) => Math.max(0, prev - sizeToDelete));
      }
    } catch (error) {
      console.error('Error deleting photo: ', error);
      alert('Error eliminando foto');
    }
  };

  const handleUpdateAlbum = async () => {
    if (!selectedAlbum) return;
    try {
      const albumRef = doc(db, 'albums', selectedAlbum.id);
      await updateDoc(albumRef, {
        title: editTitle,
        category: editCategory,
      });

      const updatedAlbum = {
        ...selectedAlbum,
        title: editTitle,
        category: editCategory,
      };
      updateLocalState(updatedAlbum);
      alert('Información actualizada');
    } catch (error) {
      console.error('Error updating album: ', error);
      alert('Error actualizando album');
    }
  };

  const handleDeleteAlbum = async () => {
    if (!selectedAlbum) return;
    if (!window.confirm('Se eliminará el album y todas sus fotos. ¿Confirmar?'))
      return;

    try {
      let totalSizeToDelete = 0;

      if (selectedAlbum.images && selectedAlbum.images.length > 0) {
        const deletePromises = selectedAlbum.images.map(async (url) => {
          const storageRef = ref(storage, url);
          try {
            const metadata = await getMetadata(storageRef);
            totalSizeToDelete += metadata.size;
          } catch (e) {
            console.warn('no metadata');
          }

          return deleteObject(storageRef).catch((err) =>
            console.warn('Failed to delete img', err)
          );
        });
        await Promise.all(deletePromises);
      }

      await deleteDoc(doc(db, 'albums', selectedAlbum.id));
      setAlbums(albums.filter((a) => a.id !== selectedAlbum.id));

      // Update Stats
      if (totalSizeToDelete > 0) {
        const statsRef = doc(db, 'content_stats', 'usage');
        await setDoc(
          statsRef,
          {
            totalBytes: increment(-totalSizeToDelete),
          },
          { merge: true }
        );
        setTotalBytes((prev) => Math.max(0, prev - totalSizeToDelete));
      }

      setSelectedAlbum(null);
    } catch (error) {
      console.error('Error deleting album: ', error);
      alert('Error eliminando album');
    }
  };

  const updateLocalState = (updatedAlbum) => {
    setSelectedAlbum(updatedAlbum);
    setAlbums(albums.map((a) => (a.id === updatedAlbum.id ? updatedAlbum : a)));
  };

  return (
    <div className="admin-layout">
      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>MORENA</h2>
          <span>CMS</span>
          <div className="storage-usage">
            <small>Uso de disco:</small>
            <strong>{formatBytes(totalBytes)}</strong>
            <button
              className="sync-btn"
              onClick={handleSyncUsage}
              title="Recalcular"
            >
              ↻
            </button>
          </div>
        </div>

        <div className="create-widget">
          <h3>NUEVO ALBUM</h3>
          <form onSubmit={handleCreateAlbum}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nombre del Cliente"
              required
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="productos">Productos</option>
              <option value="moda">Moda</option>
              <option value="gastronomia">Gastronomía</option>
              <option value="sociales">Sociales</option>
            </select>
            <button type="submit" disabled={creating}>
              {creating ? '...' : '+ CREAR'}
            </button>
          </form>
        </div>

        <div className="sidebar-footer">
          <button onClick={handleLogout}>CERRAR SESIÓN</button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="admin-content">
        <header className="content-header">
          <h1>Mis Albumes</h1>
          <div className="stats">
            <span>{albums.length} Proyectos</span>
          </div>
        </header>

        <div className="albums-grid">
          {albums.map((album) => (
            <div
              key={album.id}
              className="album-card-premium"
              onClick={() => setSelectedAlbum(album)}
            >
              <div className="card-cover">
                {album.images && album.images.length > 0 ? (
                  <img src={album.images[0]} alt="cover" />
                ) : (
                  <div className="placeholder-cover">Needs Photos</div>
                )}
                <div className="card-overlay">
                  <span>EDITAR</span>
                </div>
              </div>
              <div className="card-info">
                <h3>{album.title}</h3>
                <span className="category-tag">{album.category}</span>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* EDIT MODAL OVERLAY */}
      {selectedAlbum && (
        <div
          className="edit-overlay-backdrop"
          onClick={() => setSelectedAlbum(null)}
        >
          <div
            className="edit-overlay-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overlay-header">
              <h2>Editar Proyecto</h2>
              <button
                className="close-btn"
                onClick={() => setSelectedAlbum(null)}
              >
                ×
              </button>
            </div>

            <div className="overlay-body">
              <div className="meta-section">
                <div className="input-group">
                  <label>NOMBRE DEL PROYECTO</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label>CATEGORÍA</label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                  >
                    <option value="productos">Productos</option>
                    <option value="moda">Moda</option>
                    <option value="gastronomia">Gastronomía</option>
                    <option value="sociales">Sociales</option>
                  </select>
                </div>
                <div className="actions-row">
                  <button onClick={handleUpdateAlbum} className="btn-primary">
                    GUARDAR CAMBIOS
                  </button>
                  <button onClick={handleDeleteAlbum} className="btn-danger">
                    ELIMINAR PROYECTO
                  </button>
                </div>
              </div>

              <div className="photos-section">
                <div className="section-title">
                  <h3>GALERÍA ({selectedAlbum.images?.length || 0})</h3>
                  <label className="upload-btn-label">
                    + AGREGAR FOTOS
                    <input
                      type="file"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploading}
                      hidden
                    />
                  </label>
                </div>

                {uploading && <div className="progress-bar">SUBIENDO...</div>}

                <div className="photos-grid-premium">
                  {selectedAlbum.images?.map((url, idx) => {
                    const isVideo =
                      url.includes('.mp4') ||
                      url.includes('.mov') ||
                      url.includes('.webm');
                    return (
                      <div key={idx} className="photo-item">
                        {isVideo ? (
                          <video src={url} controls muted />
                        ) : (
                          <img src={url} alt="album content" />
                        )}
                        <div className="photo-actions">
                          <button onClick={() => handleDeletePhoto(url)}>
                            ELIMINAR
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(!selectedAlbum.images ||
                    selectedAlbum.images.length === 0) && (
                    <div className="empty-state">
                      No hay fotos ni videos en este album.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
